import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Bot, Check, Lock, Minus, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/contexts';
import { SpotlightCard, Stagger, StaggerItem } from '@/components/landing/motion-primitives';
import {
  CtaSection,
  FaqSection,
  PageHero,
  Section,
  SectionHeading,
} from '@/components/landing/page-kit';

interface PricingTier {
  name: string;
  description: string;
  price: number;
  currency: string;
  period: string;
  cta: string;
  ctaHref: string;
  highlighted: boolean;
  features: Array<{ name: string; included: boolean }>;
}

export default function PricingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const featureNames = [
    t('pricing.features.accounts' as any),
    t('pricing.features.transactions' as any),
    t('pricing.features.budgets' as any),
    t('pricing.features.multiCurrency' as any),
    t('pricing.features.reports' as any),
    t('pricing.features.aiChat' as any),
    t('pricing.features.goals' as any),
    t('pricing.features.recurring' as any),
  ];

  const pricingTiers: PricingTier[] = [
    {
      name: t('pricing.free.name' as any),
      description: t('pricing.free.description' as any),
      price: 0,
      currency: '€',
      period: '/mois',
      cta: user ? t('nav.dashboard' as any) : t('nav.register' as any),
      ctaHref: user ? '/dashboard' : '/register',
      highlighted: false,
      // Les quatre dernières fonctionnalités sont réservées au plan premium.
      features: featureNames.map((name, i) => ({ name, included: i < 4 })),
    },
    {
      name: t('pricing.premium.name' as any),
      description: t('pricing.premium.description' as any),
      price: 6,
      currency: '€',
      period: '/mois',
      cta: user ? t('nav.upgrade' as any) : t('nav.register' as any),
      ctaHref: user ? '/settings' : '/register',
      highlighted: true,
      features: featureNames.map((name) => ({ name, included: true })),
    },
  ];

  const highlights = [
    {
      icon: Zap,
      title: t('pricing.highlights.fast' as any),
      description: t('pricing.highlights.fastDesc' as any),
    },
    {
      icon: Lock,
      title: t('pricing.highlights.secure' as any),
      description: t('pricing.highlights.secureDesc' as any),
    },
    {
      icon: BarChart3,
      title: t('pricing.highlights.analytics' as any),
      description: t('pricing.highlights.analyticsDesc' as any),
    },
    {
      icon: Bot,
      title: t('pricing.highlights.ai' as any),
      description: t('pricing.highlights.aiDesc' as any),
    },
  ];

  const faqs = [
    { q: t('pricing.faqQ1' as any), a: t('pricing.faqA1' as any) },
    { q: t('pricing.faqQ2' as any), a: t('pricing.faqA2' as any) },
    { q: t('pricing.faqQ3' as any), a: t('pricing.faqA3' as any) },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingHeader />

      <main>
        <PageHero
          title={t('pricing.hero.title' as any)}
          subtitle={t('pricing.hero.subtitle' as any)}
        />

        {/* ── Offres ─────────────────────────────────────────────── */}
        <section className="border-b border-border px-6 pb-24 lg:pb-32">
          <Stagger className="mx-auto grid max-w-4xl gap-4 md:grid-cols-2" stagger={0.1}>
            {pricingTiers.map((tier) => (
              <StaggerItem key={tier.name}>
                <SpotlightCard
                  className={`flex h-full flex-col rounded-xl border bg-card p-8 transition-shadow duration-300 hover:shadow-lg ${
                    tier.highlighted ? 'border-primary/50' : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-medium">{tier.name}</h2>
                      <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground">
                        {tier.description}
                      </p>
                    </div>
                    {tier.highlighted && (
                      <span className="shrink-0 rounded-full border border-primary/40 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-brand-700 dark:text-brand-400">
                        {t('pricing.mostPopular' as any)}
                      </span>
                    )}
                  </div>

                  <div className="mt-8 flex items-baseline gap-1">
                    <span className="text-5xl font-semibold tracking-tight tabular-nums">
                      {tier.price}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {tier.currency}
                      {tier.period}
                    </span>
                  </div>

                  <Link to={tier.ctaHref} className="mt-8">
                    <Button
                      variant={tier.highlighted ? 'default' : 'outline'}
                      className={`group w-full gap-2 ${
                        tier.highlighted ? 'shadow-lg shadow-brand-600/20' : ''
                      }`}
                    >
                      {tier.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Button>
                  </Link>

                  <ul className="mt-8 space-y-3 border-t border-border pt-8">
                    {tier.features.map((feature) => (
                      <li key={feature.name} className="flex items-start gap-3 text-sm">
                        {feature.included ? (
                          <Check
                            className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                            aria-hidden="true"
                          />
                        ) : (
                          <Minus
                            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50"
                            aria-hidden="true"
                          />
                        )}
                        <span
                          className={
                            feature.included ? 'text-foreground' : 'text-muted-foreground/60'
                          }
                        >
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </SpotlightCard>
              </StaggerItem>
            ))}
          </Stagger>
        </section>

        {/* ── Pourquoi clevia ────────────────────────────────────── */}
        <Section>
          <SectionHeading
            title={t('pricing.whyChoose' as any)}
            subtitle={t('pricing.whyChooseDesc' as any)}
          />

          <Stagger className="mt-16 grid gap-4 sm:grid-cols-2" stagger={0.07}>
            {highlights.map((item) => (
              <StaggerItem key={item.title}>
                <SpotlightCard className="h-full rounded-xl border border-border bg-card p-7 transition-shadow duration-300 hover:shadow-lg">
                  <item.icon className="h-5 w-5 text-primary" />
                  <h3 className="mt-5 text-balance text-base font-medium">{item.title}</h3>
                  <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </SpotlightCard>
              </StaggerItem>
            ))}
          </Stagger>
        </Section>

        {/* ── FAQ ────────────────────────────────────────────────── */}
        <FaqSection title={t('pricing.faq' as any)} items={faqs} />

        {/* ── CTA final ──────────────────────────────────────────── */}
        <CtaSection
          title={t('pricing.ctaTitle' as any)}
          subtitle={t('pricing.ctaDesc' as any)}
          action={{
            label: user ? t('nav.dashboard' as any) : t('nav.register' as any),
            to: user ? '/dashboard' : '/register',
          }}
          secondaryAction={{ label: t('pricing.learnMore' as any), to: '/help' }}
        />
      </main>

      <LandingFooter />
    </div>
  );
}
